import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect } from 'react'
import { Container, Form, Modal, Button } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { contactUs, fetchSendEmailCode, getVerifyCode } from '../api/api'
import Footer from '../components/footer'
import Header from '../components/header'
import { LoadingRow } from '../components/loading-row'
import { FooterWapper } from '../components/styles/Footer.styled'
import { Card, MyButton } from '../components/styles/MyComponents.styled'
import { ContentPage } from '../components/styles/Page.styled'

export const ContactUs = () => {
    const [submitStatus, setSubmitStatus] = useState(false);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [message, setMessage] = useState('');
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [sendingCode, setSendingCode] = useState(false);
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState(false);
    const [codeErrorMessage, setCodeErrorMessage] = useState('');
    const [countdown, setCountdown] = useState(0);

    const handleSubmit = (event: any) => {
        event.preventDefault();

        if (name == null || name.trim() === "") {
            toast.error('Please enter your name.');
            return;
        }
        if (email == null || email.trim() === "") {
            toast.error('Please enter the correct email format.');
            return;
        }
        if (company == null || company.trim() === "") {
            toast.error('Please enter your company.');
            return;
        }
        if (message == null || message.trim() === "") {
            toast.error('Please enter your message.');
            return;
        }

        setShowVerifyModal(true);
        sendVerificationCode(email);
    }

    const sendVerificationCode = async (email: string) => {
        setSendingCode(true);
        setCodeError(false);
        setCodeErrorMessage('');
        try {
            await fetchSendEmailCode(email);
            setCountdown(90);
        } catch (err: any) {
            setCodeErrorMessage(err.message || 'Failed to send code.');
            setCodeError(true);
        } finally {
            setSendingCode(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!code || code.length < 6) {
            setCodeErrorMessage('Please enter a valid verification code.');
            setCodeError(true);
            return;
        }

        setVerifying(true);
        try {
            const res = await getVerifyCode('email', email, code);
            if (res?.status === '1') {
                setShowVerifyModal(false);
                submitContactForm();
            } else {
                setCodeErrorMessage('Invalid verification code.');
                setCodeError(true);
            }
        } catch (err: any) {
            setCodeErrorMessage(err.message || 'Verification failed.');
            setCodeError(true);
        } finally {
            setVerifying(false);
        }
    };

    const submitContactForm = () => {
        setLoading(true);
        contactUs(name, email, company, message).then((res) => {
            if (res.status === '1') {
                setSubmitStatus(true);
                toast.success('Submit Successfully.');
            } else {
                toast.error('Network error, please try again');
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    return (
        <FooterWapper>
            <Header />
            <ContentPage>
                <Container>
                    <h1>ContactUs</h1>
                    <h2>Our team is happy to answer your questions.Fill out the form and we'll be in touch as soon as possible</h2>

                    <Card >
                        {!submitStatus && !loading && (
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Your Name</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='name'
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type='email'
                                        name='email'
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Company</Form.Label>
                                    <Form.Control
                                        type='text'
                                        name='company'
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name='message'
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                    />
                                </Form.Group>

                                <div className='d-flex justify-content-center mt-5'>
                                    <MyButton type='submit'
                                        style={{
                                            width: 100,
                                            height: 36,
                                            padding: 5
                                        }}
                                        bkcolor='#00bdd6'
                                        disabled={loading}
                                    >
                                        {loading ? 'Processing...' : 'Submit'}
                                    </MyButton>
                                </div>
                            </Form>
                        )}

                        {loading && <LoadingRow />}

                        {submitStatus && (
                            <div style={{ minHeight: 300 }} className='d-flex justify-content-center align-items-center gap-3'>
                                <FontAwesomeIcon icon='check-circle' style={{ color: '#89cb6d', height: 30 }} />
                                <div>
                                    <div className='mb-3' style={{ fontSize: 20 }}>Submit Successfully.</div>
                                    <div style={{ fontSize: 20 }}>We will contact you ASAP.</div>
                                </div>
                            </div>
                        )}
                    </Card>
                </Container>
            </ContentPage>

            <Footer />

            {/* Verification Modal */}
            <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Email Verification</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>We sent code to <strong>{email}</strong>.</p>
                    <div className="mt-3">
                        <Form.Label>Two-Step Verification</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                placeholder="Enter the email verification code..."
                                value={code}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setCodeError(false);
                                    }
                                    setCode(e.target.value);
                                }}
                                disabled={verifying}
                                maxLength={6}
                            />
                            <Button
                                variant="primary"
                                disabled={countdown > 0 || sendingCode}
                                onClick={() => sendVerificationCode(email)}
                                style={{ whiteSpace: 'nowrap',color: 'white' }}
                            >
                                {sendingCode ? 'Sending...' : countdown > 0 ? `${countdown}s` : 'Resend Code'}
                            </Button>
                        </div>
                        {codeError && (
                            <div className="text-danger mt-2">
                                {codeErrorMessage}
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleVerifyCode}
                        disabled={verifying}
                        style={{ color: '#fff' }}
                    >
                        {verifying ? 'Verifying...' : 'Verify & Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </FooterWapper>
    )
}